import type { ReactElement } from "react";
import { useMemo } from "react";

import { translate } from "app/filters/i18nFilter";
import type { ChipItem } from "app/pages/sales-intelligence/common-components/filters-panel";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { LimitedChipItems } from "app/pages/sales-intelligence/common-components/filters-panel";
import { FilterResultContainer } from "app/pages/sales-intelligence/common-components/filters-panel/components/styles";

import { MAX_CHIPS_GROUP_AMOUNT } from "../../../constants";
import type FindAdSpendVolumeFilter from "../../../models/AdSpendVolumeFilter/AdSpendVolumeFilter";
import type { IAdSpendVolumeFilterDto } from "../../../types/leadsSearchFilters.dto";
import { formatMoney, formatNumber, formatPercent } from "../../../utils/numberFormatters";
import { getFormattedRangeValue } from "../../../utils/rangeValueFormatters";
import { getFilterChipTranslationKey } from "../../../utils/translationUtils";

const AdSpendVolumeFilterResult = ({
    filter: { options },
    value,
    isExpanded,
    isActionEnabled = true,
    onGetLifecycleStatus,
    onFilterUpdate,
    onFilterValueRemove,
}: IFilterViewBaseProps<FindAdSpendVolumeFilter>): ReactElement | null => {
    const handleClose = (filterKey: keyof IAdSpendVolumeFilterDto) => {
        const mappedValue = { ...value };
        delete mappedValue[filterKey];

        onFilterUpdate(mappedValue);
        onFilterValueRemove?.(mappedValue);
    };

    const chipItems = useMemo(() => {
        const items: ChipItem[] = [];

        if (value?.spend) {
            const fieldName = translate(getFilterChipTranslationKey("spend"));
            items.push({
                id: "spend",
                text: `${fieldName}: ${getFormattedRangeValue(formatMoney, options.adSpendBuckets, value.spend)}`,
                chipStatus: onGetLifecycleStatus?.({ spend: value.spend }),
            });
        }

        if (value?.activeCampaigns) {
            const fieldName = translate(getFilterChipTranslationKey("activeCampaigns"));
            items.push({
                id: "activeCampaigns",
                text: `${fieldName}: ${getFormattedRangeValue(formatNumber, options.adsActiveCampaigns, value.activeCampaigns)}`,
                chipStatus: onGetLifecycleStatus?.({ activeCampaigns: value.activeCampaigns }),
            });
        }

        if (value?.adsNumber) {
            const fieldName = translate(getFilterChipTranslationKey("adsNumber"));
            items.push({
                id: "adsNumber",
                text: `${fieldName}: ${getFormattedRangeValue(formatNumber, options.adsNumber, value.adsNumber)}`,
                chipStatus: onGetLifecycleStatus?.({ adsNumber: value.adsNumber }),
            });
        }

        if (value?.mobileShare) {
            const fieldName = translate(getFilterChipTranslationKey("mobileShare"));
            items.push({
                id: "mobileShare",
                text: `${fieldName}: ${getFormattedRangeValue(formatPercent, options.displayAdsShare, value.mobileShare)}`,
                chipStatus: onGetLifecycleStatus?.({ mobileShare: value.mobileShare }),
            });
        }

        return items;
    }, [options, value, onGetLifecycleStatus]);

    if (!chipItems.length) {
        return null;
    }

    return (
        <FilterResultContainer>
            <LimitedChipItems
                items={chipItems}
                onDelete={(key) => handleClose(key as keyof IAdSpendVolumeFilterDto)}
                maxItems={MAX_CHIPS_GROUP_AMOUNT}
                hideActionButton={!isActionEnabled}
                isTabExpanded={isExpanded}
            />
        </FilterResultContainer>
    );
};

export default AdSpendVolumeFilterResult;
